/**
 * Internal Hook: useMeetingsListQuery
 *
 * Encapsulates the TanStack Query logic for fetching the list of scheduled meetings.
 * It also handles the logic for background fetching after the initial page load.
 */
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useUser } from '../../UserContext';
import { SCHEDULED_MEETINGS_QUERY_KEYS } from '../helpers';
import type { ScheduledMeeting, ScheduledMeetingsProviderOptions } from '../types';
import useScheduledMeetingsApi from './useScheduledMeetingsApi';

const DEFAULT_PAGE_SIZE = 2;

const useMeetingsListQuery = (options: ScheduledMeetingsProviderOptions) => {
  const { isAuthenticated } = useUser();
  const api = useScheduledMeetingsApi();
  const backgroundFetchTriggered = useRef(false);

  const pageSize = useMemo(() => {
    const limit = options.initialQuery?.limit;
    return typeof limit === 'number' && limit > 0 ? limit : DEFAULT_PAGE_SIZE;
  }, [options.initialQuery?.limit]);

  const queryResult = useInfiniteQuery<
    ScheduledMeeting[],
    Error,
    InfiniteData<ScheduledMeeting[]>,
    ReturnType<typeof SCHEDULED_MEETINGS_QUERY_KEYS.list>,
    number
  >({
    queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery),
    queryFn: async ({ pageParam = 0 }) => {
      if (!isAuthenticated) {
        return [];
      }

      const response = await api.fetchMeetingsList({
        ...options.initialQuery,
        start: pageParam,
        limit: pageSize,
      });

      return response as ScheduledMeeting[];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < pageSize) {
        return undefined;
      }

      const totalFetched = allPages.reduce((acc, page) => acc + page.length, 0);

      return totalFetched;
    },
    enabled: isAuthenticated && options.autoFetch,
    staleTime: options.staleTime,
    gcTime: options.cacheTime,
    refetchInterval: options.refetchInterval,
  });

  /**
   * Background fetch after page load
   */
  useEffect(() => {
    const canBackgroundFetch =
      isAuthenticated &&
      options.backgroundFetch &&
      !options.autoFetch &&
      !backgroundFetchTriggered.current;

    if (canBackgroundFetch) {
      backgroundFetchTriggered.current = true;
      const timer = setTimeout(() => {
        queryResult.refetch();
      }, options.backgroundFetchDelay);
      return () => clearTimeout(timer);
    }
  }, [
    isAuthenticated,
    options.backgroundFetch,
    options.autoFetch,
    options.backgroundFetchDelay,
    queryResult,
  ]);

  return queryResult;
};

export default useMeetingsListQuery;
